import { AlertTriangle, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

/**
 * Styled confirmation instead of the browser's confirm(): ask("Delete Lobby TV? …", () => remove()).
 * The first sentence is the title, the rest the explanation; the button is named after the first
 * word (Delete, Remove, Reboot…) and is red for destructive actions.
 */
interface Request {
  title: string;
  message: string;
  action: string;
  danger: boolean;
  run: () => void;
}

let show: ((r: Request) => void) | null = null;

const DESTRUCTIVE = /^(delete|remove|revoke|reboot|end|check out)\b/i;

export function ask(text: string, run: () => unknown, opts: { title?: string; action?: string; danger?: boolean } = {}) {
  const m = /^(.*?[?.!])\s+(.*)$/s.exec(text.trim());
  const title = opts.title ?? (m ? m[1]! : text);
  const message = opts.title ? text : m ? m[2]! : '';
  const verb = (/^(check out|\w+)/i.exec(title)?.[1] ?? 'OK').replace(/^\w/, (c) => c.toUpperCase());
  const req: Request = { title, message, action: opts.action ?? verb, danger: opts.danger ?? DESTRUCTIVE.test(title), run: () => void run() };
  if (show) show(req);
  else if (window.confirm(text)) req.run();
}

export function ConfirmHost() {
  const [req, setReq] = useState<Request | null>(null);
  useEffect(() => {
    show = setReq;
    return () => {
      show = null;
    };
  }, []);
  useEffect(() => {
    if (!req) return;
    // Escape cancels; there is no Enter shortcut, so a stray key press never deletes anything.
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setReq(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [req]);
  if (!req) return null;
  return (
    <div className="modal-backdrop confirm-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setReq(null)}>
      <div className="modal confirm" role="alertdialog" aria-label={req.title}>
        <div className="confirm-body">
          <span className={`confirm-icon${req.danger ? ' danger' : ''}`}>{req.danger && /^(delete|remove)/i.test(req.action) ? <Trash2 size={22} /> : <AlertTriangle size={22} />}</span>
          <div>
            <h2>{req.title}</h2>
            {req.message && <p>{req.message}</p>}
          </div>
        </div>
        <div className="confirm-foot">
          <button className="btn" onClick={() => setReq(null)} autoFocus>
            Cancel
          </button>
          <button
            className={`btn ${req.danger ? 'btn-danger-solid' : 'primary'}`}
            onClick={() => {
              req.run();
              setReq(null);
            }}
          >
            {req.action}
          </button>
        </div>
      </div>
    </div>
  );
}
